import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { orgHomePath } from "@shared/appUrls";

import { useAuth } from "@/contexts/AuthContext";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { getCurrentUser } from "@/lib/auth";
import { APP_ROUTES, splitOrgPath } from "@/lib/appRoutes";
import { resolveOrgSwitchTarget } from "@/lib/organizationSwitchTarget";
import { probeOrgDoc } from "@/services/firebase";
import type { OrganizationSummary } from "@/types/provisionUser";

const isAdminDashboardPath = (pathname: string): boolean =>
  pathname === APP_ROUTES.adminDashboard || pathname.startsWith(`${APP_ROUTES.adminDashboard}/`);

/**
 * Switches to `org` the way the header picker does. The URL names the active
 * org, so on an org page this only navigates, and App's URL sync selects the
 * org: it pushes the same page in `org` when its documents exist there (a
 * section always does), otherwise `org`'s home, so Back returns to the
 * previous org. The cross-org admin dashboard has no org in its URL, so there
 * it selects `org` in place. Any other page opens `org`'s home. The org already
 * selected is a no-op.
 */
export function useOrganizationSwitch(): {
  switchOrganization: (org: OrganizationSummary) => Promise<void>;
  switching: boolean;
} {
  const { selectedOrganization, setSelectedOrganization } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Lets a pending switch notice that the user navigated, or left this switcher's
  // screen (e.g. for a full-screen route without the header), while it was checking.
  const beginNavigationGuard = useNavigationGuard();
  const [switching, setSwitching] = useState(false);
  const selectedOrgId = selectedOrganization?.id;

  const switchOrganization = useCallback(
    async (org: OrganizationSummary) => {
      if (!org.id || org.id === selectedOrgId) {
        return;
      }
      if (isAdminDashboardPath(pathname)) {
        setSelectedOrganization(org);
        return;
      }
      if (!splitOrgPath(pathname)) {
        navigate(orgHomePath(org.id));
        return;
      }

      setSwitching(true);
      const stillHere = beginNavigationGuard();
      // Read from the auth singleton, not context: it stays current even if this
      // hook's component unmounted (e.g. on sign-out) while the check was pending.
      const uid = getCurrentUser()?.uid;
      try {
        const target = await resolveOrgSwitchTarget(pathname, org.id, (path) => probeOrgDoc(org.id, path));
        // Drop the switch if, during the check, the user navigated elsewhere (their
        // navigation wins over a destination computed for the old page) or left this
        // switcher's screen, signed out, or another account signed in.
        if (!stillHere() || !uid || getCurrentUser()?.uid !== uid) {
          return;
        }
        navigate(target);
      } finally {
        setSwitching(false);
      }
    },
    [selectedOrgId, pathname, navigate, setSelectedOrganization, beginNavigationGuard]
  );

  return { switchOrganization, switching };
}
